import { type UserModel } from "../type/user";
import { EXPIRY_KEY, TOKEN_KEY, USER_KEY } from "../utils/config";
import { getApiUrl } from "../utils/getApiUrl";
import { normalizeParameters } from "../utils/usersParameters";

export async function fetchUser(): Promise<UserModel | null> {
  try {
    if (!isAuthenticated()) {return null;}

    const resp = await fetchWithAuth(getApiUrl("user/get"), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!resp.ok) return null;
    const data = await resp.json();

    const user: UserModel = {
      ...data,
      parameters: normalizeParameters(data.parameters),
    };

    setUser(user); 
    return user;
  } catch(e: unknown) {
    console.log(e);
    return null;
  }
}

export const setToken = (token: string, days = 30) => {
  localStorage.setItem(TOKEN_KEY, token);
  // store expiry timestamp (ms)
  const expiryMs = Date.now() + days * 24 * 60 * 60 * 1000;
  localStorage.setItem(EXPIRY_KEY, String(expiryMs));
};

export const getToken = (): string | null => {
  const token = localStorage.getItem(TOKEN_KEY);
  const exp = localStorage.getItem(EXPIRY_KEY);
  if (!token || !exp) {
    clearAuth();
    return null;
  }
  const expMs = Number(exp);
  if (Number.isNaN(expMs) || Date.now() > expMs) {
    // expired
    clearAuth();
    return null;
  }
  return token;
};

export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(EXPIRY_KEY);
};

export function isAuthenticated() {
  return localStorage.getItem(TOKEN_KEY) !== null;
}

export const setUser = (user: UserModel) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const getUser = (): UserModel | null => {
  const v = localStorage.getItem(USER_KEY);
  return v ? JSON.parse(v) : null;
};

// helper: get expiry timestamp (ms) or null
export const getTokenExpiry = (): number | null => {
  const v = localStorage.getItem(EXPIRY_KEY);
  if (!v) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

// helper: milliseconds until expiry, or null when no token
export const timeUntilExpiry = (): number | null => {
  const e = getTokenExpiry();
  return e ? Math.max(0, e - Date.now()) : null;
};

// Should show reminder when expiry is within `daysBefore` days (default 3)
export const shouldShowLoginReminder = (daysBefore = 3): boolean => {
  const ms = timeUntilExpiry();
  if (ms === null) return false;
  const threshold = daysBefore * 24 * 60 * 60 * 1000;
  return ms <= threshold && ms > 0;
};

export async function fetchWithAuth(input: RequestInfo, init?: RequestInit) {
  const token = getToken();
  const headers = new Headers(init?.headers || {});
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const merged: RequestInit = { ...init, headers };
  return fetch(input, merged);
}
